/* ==========================================================================
   TSH mini cart — drawer behaviour
   Vanilla, no jQuery. Loaded site-wide from layout/theme.liquid.

   Division of labour with the compiled grid.js bundle (which we don't touch):

   - grid.js owns the ADD. With enable_cart_redirection set to false in
     templates/product.json, its product controller POSTs a FormData to
     /cart/add.js via fetch, handles inline error messages, then runs its own
     updateCart() which morphdom-patches .mini-cart-item-wrapper and syncs
     .cart-count-number. We let it. It also means Klaviyo's fetch monkey-patch
     in theme.liquid keeps matching /cart/add.js and "Added to Cart" tracking
     is unaffected.

   - We own everything grid.js doesn't: opening the drawer, the free-delivery
     bar, the total, the cross-sell block, quantity changes and removals.

   Because grid.js's updateCart() and our refresh() both fire on an add, the
   refresh is debounced so the two collapse into a single section render.

   Cart mutations are detected by wrapping window.fetch rather than by binding
   to buttons, so adds originating anywhere — the PDP form, the cross-sell
   cards, quickshop, a third-party app using fetch — all land in the same
   place. Apps that add over XMLHttpRequest will not be caught; if one turns
   up, wrap XHR the same way.
   ========================================================================== */

(function () {
  'use strict';

  var SELECTOR = '[data-mini-cart]';
  var REFRESH_DELAY = 120;
  var CROSSSELL_LIMIT_MOBILE = 2;
  var CROSSSELL_LIMIT_DESKTOP = 3;
  /* Must stay in step with the .mini-cart-crosssell__grid breakpoint in
     mini-cart.css, or the column count and the number of products fetched
     will disagree and leave a gap in the grid. */
  var MOBILE_QUERY = '(max-width: 660px)';

  var root = null;
  var panel = null;
  var lastFocused = null;
  var refreshTimer = null;
  var lastAddedProductId = null;
  var crossSellToken = 0;

  /* --- Utilities --------------------------------------------------------- */

  function $(sel, scope) {
    return (scope || document).querySelector(sel);
  }

  function $$(sel, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(sel));
  }

  function isMobile() {
    return window.matchMedia && window.matchMedia(MOBILE_QUERY).matches;
  }

  function routes() {
    var r = (window.Theme && window.Theme.routes) || {};
    return {
      cart: r.cart_url || '/cart',
      add: r.cart_add_url || '/cart/add',
      change: r.cart_change_url || '/cart/change'
    };
  }

  /**
   * Shopify money formatting. Prices from the recommendations API are integer
   * cents; the shop's money format may use any of Shopify's four placeholder
   * styles. Output contains HTML entities (&pound;), so it must always be
   * written with innerHTML, never textContent.
   */
  function formatMoney(cents) {
    var format = (window.Theme && window.Theme.moneyFormat) || '&pound;{{amount}}';
    var value = Number(cents) || 0;

    function withDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';
      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.');
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var fraction = parts[1] ? decimal + parts[1] : '';

      return whole + fraction;
    }

    var placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
    var name = placeholder ? placeholder[1] : 'amount';
    var formatted;

    switch (name) {
      case 'amount_no_decimals':
        formatted = withDelimiters(value, 0);
        break;
      case 'amount_with_comma_separator':
        formatted = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        formatted = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        formatted = withDelimiters(value, 2, "'", '.');
        break;
      default:
        formatted = withDelimiters(value, 2);
    }

    return format.replace(/\{\{\s*\w+\s*\}\}/, formatted);
  }

  /* --- Open / close ------------------------------------------------------ */

  function isOpen() {
    return !!root && root.classList.contains('is-open');
  }

  function open() {
    if (!root || isOpen()) return;

    lastFocused = document.activeElement;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('mini-cart-open');

    if (panel) panel.focus();
    loadCrossSell();
  }

  function close() {
    if (!root || !isOpen()) return;

    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('mini-cart-open');

    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
    lastFocused = null;
  }

  function trapFocus(event) {
    if (event.key !== 'Tab' || !isOpen() || !panel) return;

    var focusable = $$(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      panel
    ).filter(function (el) {
      return el.offsetParent !== null;
    });

    if (!focusable.length) return;

    var first = focusable[0];
    var last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* --- Refresh ----------------------------------------------------------- */

  function scheduleRefresh(options) {
    if (refreshTimer) clearTimeout(refreshTimer);
    refreshTimer = setTimeout(function () {
      refreshTimer = null;
      refresh(options);
    }, REFRESH_DELAY);
  }

  /**
   * Re-renders the drawer from /cart?view=mini-ajax, which returns
   * sections/mini-ajax.liquid — a bare render of this same snippet.
   *
   * Patches are targeted rather than a blanket innerHTML swap for one reason:
   * the express checkout buttons are injected by Shopify's own script and go
   * inert if they're ever replaced. They live in the footer and nothing below
   * touches the footer's markup, only its text.
   */
  function refresh(options) {
    options = options || {};

    return fetch(routes().cart + '?view=mini-ajax', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      credentials: 'same-origin'
    })
      .then(function (res) {
        if (!res.ok) throw new Error('mini-cart refresh failed');
        return res.text();
      })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var template = parsed.querySelector('[data-html="miniCart"]');
        var source = template ? template.content : parsed.body;

        applyPatch(source);
        if (options.open) open();
        loadCrossSell();
      })
      .catch(function (error) {
        // A failed refresh must not strand the drawer in a stale state, so
        // fall back to the cart page rather than showing wrong totals.
        if (window.console) console.warn('[mini-cart]', error);
      });
  }

  function applyPatch(source) {
    if (!root || !source) return;

    var newRoot = source.querySelector('[data-mini-cart]') || source;

    // Item list — grid.js may have already patched this with identical
    // content on an add; writing it again is idempotent.
    swapHTML('.mini-cart-item-wrapper', newRoot);
    swapHTML('[data-mini-cart-progress]', newRoot);

    var newBody = newRoot.querySelector('[data-mini-cart-body]');
    var body = $('[data-mini-cart-body]', root);
    if (newBody && body) {
      body.classList.toggle('empty', newBody.classList.contains('empty'));
    }

    var newTotal = newRoot.querySelector('[data-mini-cart-total]');
    var total = $('[data-mini-cart-total]', root);
    if (newTotal && total) total.innerHTML = newTotal.innerHTML; // entity prices

    var newCount = newRoot.querySelector('[data-mini-cart-count]');
    var count = $('[data-mini-cart-count]', root);
    var itemCount = newCount ? newCount.textContent.trim() : null;
    if (count && itemCount !== null) count.textContent = itemCount;

    if (newRoot.hasAttribute && newRoot.hasAttribute('data-total-cents')) {
      root.setAttribute('data-total-cents', newRoot.getAttribute('data-total-cents'));
    }
    if (newRoot.hasAttribute && newRoot.hasAttribute('data-anchor-product')) {
      root.setAttribute('data-anchor-product', newRoot.getAttribute('data-anchor-product'));
    }

    syncHeaderCount(itemCount);
  }

  function swapHTML(selector, source) {
    var target = $(selector, root);
    var replacement = source.querySelector(selector);
    if (target && replacement) target.innerHTML = replacement.innerHTML;
  }

  /**
   * The visible basket badge is .item-c-count in the hand-built header icon
   * row. custom.js only ever sets its state once, on page load, via a jQuery
   * getJSON — so without this it never moves after an add.
   */
  function syncHeaderCount(itemCount) {
    if (itemCount === null || typeof itemCount === 'undefined') return;

    var value = String(itemCount);
    var numeric = parseInt(value, 10) || 0;

    $$('.item-c-count').forEach(function (el) {
      el.textContent = value;
      el.classList.toggle('item-c-count-up', numeric > 0);
    });

    $$('.cart-count-number').forEach(function (el) {
      el.textContent = value;
    });
  }

  /* --- Line item quantity and removal ------------------------------------ */

  function setBusy(state) {
    if (root) root.classList.toggle('is-busy', !!state);
  }

  function changeLine(key, quantity) {
    setBusy(true);

    return fetch(routes().change + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (res) {
        if (!res.ok) throw new Error('cart change failed');
        return res.json();
      })
      .then(function () {
        return refresh();
      })
      .catch(function (error) {
        if (window.console) console.warn('[mini-cart]', error);
        return refresh();
      })
      .then(function () {
        setBusy(false);
      });
  }

  function handleLineClick(event) {
    var removeBtn = event.target.closest('[data-mini-cart-remove]');
    var qtyBtn = event.target.closest('[data-mini-cart-qty-step]');
    if (!removeBtn && !qtyBtn) return;

    var item = event.target.closest('[data-mini-cart-item]');
    if (!item) return;

    var key = item.getAttribute('data-line-key');
    if (!key) return;

    event.preventDefault();

    if (removeBtn) {
      changeLine(key, 0);
      return;
    }

    var valueEl = $('[data-mini-cart-qty-value]', item);
    var current = parseInt(valueEl ? valueEl.textContent : '1', 10) || 1;
    var step = parseInt(qtyBtn.getAttribute('data-mini-cart-qty-step'), 10) || 0;
    var next = current + step;

    if (next < 1) {
      changeLine(key, 0);
    } else {
      changeLine(key, next);
    }
  }

  /* --- Cross-sell -------------------------------------------------------- */

  function cartProductIds() {
    return $$('[data-mini-cart-item]', root).map(function (el) {
      return parseInt(el.getAttribute('data-product'), 10);
    }).filter(Boolean);
  }

  function cartTotalCents() {
    return parseInt(root.getAttribute('data-total-cents'), 10) || 0;
  }

  function fallbackPool() {
    var script = $('[data-mini-cart-fallback]', root);
    if (!script) return [];
    try {
      return JSON.parse(script.textContent) || [];
    } catch (error) {
      return [];
    }
  }

  function fetchRecommendations(productId, intent, limit) {
    var url = '/recommendations/products.json?product_id=' + encodeURIComponent(productId) +
      '&limit=' + limit + '&intent=' + intent;

    return fetch(url, { credentials: 'same-origin' })
      .then(function (res) {
        return res.ok ? res.json() : { products: [] };
      })
      .then(function (data) {
        return (data && data.products) || [];
      })
      .catch(function () {
        return [];
      });
  }

  /**
   * One-tap "Add to Bag" has to know exactly which variant it's adding, so
   * anything with more than one variant is excluded rather than guessed at.
   * Sold-out items and anything already in the bag are dropped too.
   */
  function isEligible(product, inCart) {
    if (!product || product.available === false) return false;
    if (inCart.indexOf(product.id) !== -1) return false;

    if (product.variants) {
      if (product.variants.length !== 1) return false;
      if (product.variants[0].available === false) return false;
    } else if (!product.variant_id) {
      return false;
    }

    return true;
  }

  function variantIdOf(product) {
    if (product.variant_id) return product.variant_id;
    return product.variants && product.variants[0] && product.variants[0].id;
  }

  function pushUnique(list, product, limit) {
    if (list.length >= limit) return;
    var exists = list.some(function (p) { return p.id === product.id; });
    if (!exists) list.push(product);
  }

  /**
   * Relevance wins, price is a filter within the relevant pool.
   *
   * 1. Curated complementary products, filtered to what fits the gap.
   * 2. Top up from the merchandised fallback collection, ranked by how
   *    closely each item closes the gap.
   * 3. Top up from the complementary pool again, price filter relaxed.
   * 4. Last resort, Shopify's algorithmic related products.
   *
   * Picking cheapest-first instead would reliably surface an £8 item that has
   * nothing to do with what's in the bag, which sells nothing.
   */
  function loadCrossSell() {
    if (!root || root.getAttribute('data-crosssell-enabled') !== 'true') return;

    var wrapper = $('[data-mini-cart-crosssell]', root);
    var grid = $('[data-mini-cart-crosssell-grid]', root);
    if (!wrapper || !grid) return;

    var anchor = lastAddedProductId || root.getAttribute('data-anchor-product');
    if (!anchor) {
      wrapper.hidden = true;
      return;
    }

    var limit = isMobile() ? CROSSSELL_LIMIT_MOBILE : CROSSSELL_LIMIT_DESKTOP;
    var threshold = parseInt(root.getAttribute('data-threshold'), 10) || 0;
    var tolerance = parseInt(root.getAttribute('data-tolerance'), 10) || 0;
    var gap = threshold - cartTotalCents();
    var inCart = cartProductIds();
    var token = ++crossSellToken;

    function inBudget(product) {
      if (gap <= 0) return true;
      return product.price <= gap + tolerance;
    }

    fetchRecommendations(anchor, 'complementary', 10).then(function (complementary) {
      var pool = complementary.filter(function (p) { return isEligible(p, inCart); });
      var picks = [];

      pool.filter(inBudget).forEach(function (p) { pushUnique(picks, p, limit); });

      if (picks.length < limit) {
        fallbackPool()
          .filter(function (p) { return isEligible(p, inCart) && inBudget(p); })
          .sort(function (a, b) {
            return Math.abs(a.price - gap) - Math.abs(b.price - gap);
          })
          .forEach(function (p) { pushUnique(picks, p, limit); });
      }

      if (picks.length < limit) {
        pool.forEach(function (p) { pushUnique(picks, p, limit); });
      }

      if (picks.length >= limit) {
        if (token === crossSellToken) renderCrossSell(picks.slice(0, limit));
        return;
      }

      return fetchRecommendations(anchor, 'related', 10).then(function (related) {
        related
          .filter(function (p) { return isEligible(p, inCart); })
          .forEach(function (p) { pushUnique(picks, p, limit); });

        /*  Last resort: the fallback collection again, this time without the
            budget filter. Step 2 only ever considers it for items that fit the
            remaining spend, so with a small gap — an £85 cart leaves £15, and
            a £35 ceiling — a collection of £40 add-ons is excluded there and
            never looked at again. Relevance has already been exhausted by this
            point, so a merchandised add-on beats leaving the row short. */
        if (picks.length < limit) {
          fallbackPool()
            .filter(function (p) { return isEligible(p, inCart); })
            .sort(function (a, b) {
              return Math.abs(a.price - gap) - Math.abs(b.price - gap);
            })
            .forEach(function (p) { pushUnique(picks, p, limit); });
        }

        if (token === crossSellToken) renderCrossSell(picks.slice(0, limit));
      });
    });
  }

  function renderCrossSell(products) {
    var wrapper = $('[data-mini-cart-crosssell]', root);
    var grid = $('[data-mini-cart-crosssell-grid]', root);
    if (!wrapper || !grid) return;

    if (!products.length) {
      wrapper.hidden = true;
      grid.innerHTML = '';
      return;
    }

    /*  The column count follows what was actually found, not what was asked
        for. The cascade can come up short — the one-size-only rule removes
        every sized product, which on this catalogue is most of them — and a
        three-column grid holding two cards reads as a missing product rather
        than as two. Set as an attribute rather than an inline style so the
        mobile media query still applies. */
    grid.setAttribute('data-count', String(products.length));

    grid.innerHTML = products.map(function (product) {
      var image = product.featured_image || (product.images && product.images[0]) || '';
      var variantId = variantIdOf(product);
      var url = product.url || '#';

      return (
        '<div class="mini-cart-crosssell__card">' +
          '<a class="mini-cart-crosssell__image" href="' + escapeAttr(url) + '" tabindex="-1" aria-hidden="true">' +
            (image ? '<img src="' + escapeAttr(sizedImage(image, 400)) + '" alt="" loading="lazy">' : '') +
          '</a>' +
          '<p class="mini-cart-crosssell__name">' +
            '<a href="' + escapeAttr(url) + '">' + escapeHTML(product.title) + '</a>' +
          '</p>' +
          '<p class="mini-cart-crosssell__price">' + formatMoney(product.price) + '</p>' +
          '<button type="button" class="mini-cart-crosssell__add" data-mini-cart-add="' +
            escapeAttr(String(variantId)) + '"><span>Add to bag</span></button>' +
        '</div>'
      );
    }).join('');

    /* The cascade can return fewer than the limit — most of this catalogue is
       sized, and sized products are excluded because one-tap add can't pick a
       variant. Publishing the real count lets the grid collapse to it rather
       than leaving an empty track. */
    grid.setAttribute('data-count', String(products.length));
    wrapper.hidden = false;
  }

  function sizedImage(src, width) {
    if (!src) return '';
    if (src.indexOf('width=') !== -1) return src;
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function escapeHTML(value) {
    var div = document.createElement('div');
    div.textContent = value == null ? '' : String(value);
    return div.innerHTML;
  }

  function escapeAttr(value) {
    return escapeHTML(value).replace(/"/g, '&quot;');
  }

  function handleCrossSellAdd(event) {
    var button = event.target.closest('[data-mini-cart-add]');
    if (!button) return;

    event.preventDefault();

    var variantId = button.getAttribute('data-mini-cart-add');
    if (!variantId) return;

    button.disabled = true;

    // Posted to /cart/add.js so the fetch wrapper below — and Klaviyo's —
    // both see it as a normal add.
    fetch(routes().add + '.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      credentials: 'same-origin',
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .catch(function (error) {
        if (window.console) console.warn('[mini-cart]', error);
      })
      .then(function () {
        button.disabled = false;
      });
  }

  /* --- Cart mutation interception ---------------------------------------- */

  function watchCartMutations() {
    if (!window.fetch) return;

    var nativeFetch = window.fetch;

    window.fetch = function () {
      var request = arguments[0];
      var url = typeof request === 'string' ? request : (request && request.url) || '';
      var response = nativeFetch.apply(this, arguments);

      if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
        response.then(function (res) {
          if (!res || !res.ok) return;

          // Clone before reading — the original body still has to reach
          // grid.js and Klaviyo intact.
          res.clone().json().then(function (data) {
            var item = data && (data.items ? data.items[0] : data);
            if (item && item.product_id) lastAddedProductId = item.product_id;
          }).catch(function () {});

          scheduleRefresh({ open: true });
        }).catch(function () {});
      } else if (/\/cart\/(change|update|clear)(\.js)?(\?|$)/.test(url)) {
        response.then(function (res) {
          if (res && res.ok) scheduleRefresh();
        }).catch(function () {});
      }

      return response;
    };
  }

  /* --- Triggers ---------------------------------------------------------- */

  function wireTriggers() {
    // The basket icon in .header-icon-section is a plain link to /cart. It
    // keeps its href so it still works without JS, but becomes the drawer
    // trigger when JS is available.
    $$('.header-icon-section a[href*="/cart"], .cart-count, .sticky-header-cart-count')
      .forEach(function (link) {
        if (link.hasAttribute('data-mini-cart-open')) return;
        link.setAttribute('data-mini-cart-open', '');
      });

    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-mini-cart-open]');
      if (opener) {
        event.preventDefault();
        open();
        return;
      }

      if (event.target.closest('[data-mini-cart-close]')) {
        event.preventDefault();
        close();
        return;
      }

      handleCrossSellAdd(event);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && isOpen()) {
        close();
        return;
      }
      trapFocus(event);
    });

    if (root) {
      root.addEventListener('click', handleLineClick);
    }
  }

  /* --- Boot -------------------------------------------------------------- */

  function init() {
    root = $(SELECTOR);
    if (!root) return;

    panel = $('[data-mini-cart-panel]', root);

    // The hidden attribute is a no-JS guard only; visibility handles the
    // closed state so the panel can transition.
    root.removeAttribute('hidden');

    wireTriggers();
    syncHeaderCount($('[data-mini-cart-count]', root).textContent.trim());
  }

  watchCartMutations();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
